import { useCallback, useRef, useState } from 'react'

import { Editor } from '@tiptap/react'
import { v4 as uuidv4 } from 'uuid'

import { useCreateFiles, useCreateImages } from '@tribeplatform/react-sdk/hooks'

import { TiptapContextType, TiptapProviderType } from '../@types'
import { SupportedMediaFormats } from '../config/constants'
import { ImageAttributes } from '../extensions/image'
import { findNode } from '../helpers/findNode'
import { composerError, fileUploadError } from '../utils/errors'

const getFileReader = async (file: File) =>
  new Promise<{ url: string }>(resolve => {
    const fr = new FileReader()
    fr.onload = () => {
      resolve({ url: fr.result as string })
    }
    fr.readAsDataURL(file)
  })

type useHandleFilesProps = {
  editor: Editor
  handleAttachmentSelect: TiptapProviderType['handleAttachmentSelect']
}

type FileWithID = File & {
  id?: string
}

export const useHandleFiles = ({
  editor,
  handleAttachmentSelect,
}: useHandleFilesProps) => {
  // We are using this stack to prevent falsifying the loading state while calling the onFileUpload function multiple times.
  const uploadQueue = useRef<number[]>([])
  const [loading, setLoading] = useState(false)
  const { mutateAsync: createImages } = useCreateImages()
  const { mutateAsync: createFiles } = useCreateFiles()

  const uploadImage = useCallback(
    async (file: FileWithID, attrs?: Partial<ImageAttributes>) => {
      const fileSrc = await getFileReader(file)

      editor
        .chain()
        .focus()
        .setImage({
          ...attrs,
          id: file.id,
          src: fileSrc.url,
          loading: true,
        })
        .setTextSelection(editor.state.selection.anchor) // to reset the selection
        .run()

      const [{ url, id }] = await createImages([{ file }])

      // before updating the src let's preload the url.
      const img = new Image()
      img.src = url

      img.onload = () => {
        const { offset, node } = findNode(
          editor,
          node => node.attrs.id === file.id,
        )
        editor
          .chain()
          .setNodeSelection(offset)
          .focus()
          .setImageAttributes({ loading: false, src: url, id })
          .setTextSelection(offset + node.nodeSize)
          .insertContent(' ')
          .run()
      }
    },
    [editor, createImages],
  )

  const uploadVideo = useCallback(
    async (file: FileWithID) => {
      const fileSrc = await getFileReader(file)
      const { type, name, size } = file

      editor
        .chain()
        .focus()
        .setVideo({ id: file.id, src: fileSrc.url, loading: true, type })
        .setTextSelection(editor.state.selection.anchor) // to reset the selection
        .run()

      const [{ id, url }] = await createFiles([
        {
          file,
          contentType: type,
          name,
          extension: name.split('.').pop().toLowerCase() || 'mp4',
          size,
        },
      ])

      const { offset, node } = findNode(
        editor,
        node => node.attrs.id === file.id,
      )

      editor
        .chain()
        .setNodeSelection(offset)
        .focus()
        .setVideoAttributes({
          loading: false,
          id,
          src: url,
          type,
        })
        .setTextSelection(offset + node.nodeSize)
        .insertContent(' ')
        .run()
    },
    [editor, createFiles],
  )

  const onFileUpload: TiptapContextType['onFileUpload'] = useCallback(
    async (filesList, options = {}) => {
      const { asAttachment = false, attrs } = options
      setLoading(true)
      uploadQueue.current.push(new Date().getTime())
      // If not image, do nothing
      if (!filesList?.length) return null

      const files = Array.from(filesList) as FileWithID[]
      await Promise.all(
        files.map(async file => {
          const size = file.size / 1024 / 1024
          if (size > 10) {
            composerError('The file size limit is 10MB.')
            return
          }

          file.id = uuidv4()

          try {
            if (!asAttachment && SupportedMediaFormats.includes(file.type)) {
              await uploadVideo(file)
            } else if (!asAttachment && file.type?.startsWith('image')) {
              await uploadImage(file, attrs)
            } else {
              await handleAttachmentSelect([file])
            }
          } catch (error) {
            // Show the error notification message
            fileUploadError({
              fileName: file.name,
              message: error.message,
            })

            if (file.id) {
              const { offset } = findNode(
                editor,
                node => node.attrs.id === file.id,
              )

              // Delete the actual node (image/video tag) from the DOM
              editor.commands.deleteRange({
                from: offset,
                to: offset + 1,
              })
            }
          }
        }),
      ).finally(() => {
        uploadQueue.current.pop()
        if (uploadQueue.current.length === 0) {
          setLoading(false)
        }
      })
    },
    [uploadVideo, uploadImage, handleAttachmentSelect, editor],
  )

  return {
    onFileUpload,
    loading,
  }
}
